import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import '../css/Auth.css';
import { createUser, credentialCheck, loginUser } from '../helpers/user';
import { useAuth } from '../context/AuthContext';

const Auth = () => {
  usePageTitle('Connexion');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: ''
  });
  const [errors, setErrors] = useState({});
  
  // Get the return URL from location state, or default to home page
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
  
    // Basic validation
    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
  
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8 && !isLogin) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }
  
    if (!isLogin) {
      if (!formData.username) {
        newErrors.username = 'Le nom d\'utilisateur est requis';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    }
  
    // If there are validation errors, show them and stop
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    // Handle form submission
    if (isLogin) {
      const response = await credentialCheck({identifier: formData.email, password: formData.password});
      // console.log(response);
      if(response){
        await loginUser(response);
        login(response);
        navigate(from, { replace: true });
        // navigate('/');
        return;
      }else{
        setErrors({general: 'E-mail ou mot de passe incorrect'});
        return;
      }
    } else {
      const { confirmPassword, ...dataToSend } = formData;
      const response = await createUser(dataToSend);
      // console.log(response)
      if (response.jwt) {
        await loginUser(response);
        login(response);
        navigate('/');
        return;
      }
  
      if (response.exists && response.exists === true) {
        if (response.data === 'email') {
          setErrors({ email: 'Cet email existe déjà' });
        } else {
          setErrors({ username: 'Ce nom d\'utilisateur est déjà pris' });
        }
        return;
      }
  
      setErrors({ general: 'Une erreur est survenue, merci de réessayer' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>{isLogin ? 'Connexion' : 'Inscription'}</h1>
          <p>{isLogin ? 
            'Ravis de vous revoir !' : 
            'Rejoignez la communauté Ti\'Spot 971'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="username">Nom d'utilisateur</label>
              <div className="input-wrapper">
                <User size={20} />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Votre nom d'utilisateur"
                />
              </div>
              {errors.username && <span className="error">{errors.username}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <Mail size={20} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
              />
            </div>
            {errors.email && <span className="error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <div className="input-wrapper">
              <Lock size={20} />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <span className="error">{errors.password}</span>}
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <div className="input-wrapper">
                <Lock size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirmer votre mot de passe"
                />
              </div>
              {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
            </div>
          )}

          {errors.general && <span className="error">{errors.general}</span>}

          <button type="submit" className="submit-button">
            {isLogin ? 'Se connecter' : 'S\'inscrire'}
          </button>
        </form>

        <div className="auth-footer">
          {isLogin ? (
            <>
              <Link to="/mot-de-passe-oublie" className="forgot-password">
                Mot de passe oublié ?
              </Link>
              <p>
                Pas encore de compte ?{' '}
                <button 
                  className="toggle-auth"
                  onClick={() => setIsLogin(false)}
                >
                  S'inscrire
                </button>
              </p>
            </>
          ) : (
            <p>
              Déjà un compte ?{' '}
              <button 
                className="toggle-auth"
                onClick={() => setIsLogin(true)}
              >
                Se connecter
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;